import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuLabel,
  DropdownMenuShortcut,
  DropdownMenuPortal,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

// Aspect-ratio choices for the "Guides" overlay. Values are width/height
// ratios so we can draw them regardless of the actual video's aspect.
export const GUIDE_RATIOS: { label: string; value: number }[] = [
  { label: "2.35", value: 2.35 },
  { label: "1.85", value: 1.85 },
  { label: "16:9", value: 16 / 9 },
  { label: "9:16", value: 9 / 16 },
  { label: "4:3",  value: 4 / 3 },
  { label: "1:1",  value: 1 },
];

export type ZoomMode = "fit" | "fill" | "custom";

export type SettingsState = {
  zoomMode: ZoomMode;
  /** Only meaningful when zoomMode === "custom". 1 = 100%. */
  zoomScale: number;
  /** Guide ratio (width/height) or null when off. */
  guidesRatio: number | null;
  /** Dim outside the guide box. Ignored when guidesRatio is null. */
  showMask: boolean;
  /** Overlay anchor pins on the video canvas (Task 13 will consume). */
  viewAnchoredComments: boolean;
  /** Overlay annotation shapes on the video canvas (Task 12 will consume). */
  viewAnnotations: boolean;
};

export const DEFAULT_SETTINGS: SettingsState = {
  zoomMode: "fit",
  zoomScale: 1,
  guidesRatio: null,
  showMask: false,
  viewAnchoredComments: true,
  viewAnnotations: false,
};

type Props = {
  value: SettingsState;
  onChange: (next: SettingsState) => void;
  /** Icon rendered inside the built-in trigger button (usually <Settings/>). */
  children: React.ReactNode;
};

/**
 * Settings dropdown (Task 6). Three items only per user spec:
 *   - Zoom → Fit / Fill / Zoom In / Zoom Out / Zoom to 100% (T/Y/+/-/Ctrl+0)
 *   - Guides → aspect ratio overlay 2.35 / 1.85 / 16:9 / 9:16 / 4:3 / 1:1 / Off + Show Mask
 *   - View on Asset → toggles: Anchored Comments + Annotations
 *
 * Cut per spec: Quality, Set Frame as Thumb, Download Still.
 *
 * Opens ABOVE the trigger (side="top") because the gear lives on the bottom
 * control bar. Wired to the parent's settings state via `value` + `onChange`.
 */
export function SettingsMenu({ value, onChange, children }: Props) {
  function patch(next: Partial<SettingsState>) {
    onChange({ ...value, ...next });
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label="Settings"
        title="Settings"
        className="grid h-8 w-8 place-items-center rounded-md text-black/70 transition-colors hover:bg-black/10 hover:text-black data-[state=open]:bg-black/10 data-[state=open]:text-black dark:text-white/80 dark:hover:bg-white/10 dark:hover:text-white dark:data-[state=open]:bg-white/10 dark:data-[state=open]:text-white"
      >
        {children}
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="end" className="w-56">
        {/* ── Zoom ────────────────────────────────────────────────────────── */}
        <DropdownMenuSub>
          <DropdownMenuSubTrigger className="justify-between">
            <span>Zoom</span>
            <span className="text-xs text-muted-foreground">{zoomLabel(value)}</span>
          </DropdownMenuSubTrigger>
          <DropdownMenuPortal>
            <DropdownMenuSubContent className="w-44">
              <DropdownMenuItem onSelect={() => patch({ zoomMode: "fit", zoomScale: 1 })}>
                Fit
                <DropdownMenuShortcut>T</DropdownMenuShortcut>
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => patch({ zoomMode: "fill", zoomScale: 1 })}>
                Fill
                <DropdownMenuShortcut>Y</DropdownMenuShortcut>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  patch({ zoomMode: "custom", zoomScale: Math.min(4, value.zoomScale * 1.25) });
                }}
              >
                Zoom In
                <DropdownMenuShortcut>+</DropdownMenuShortcut>
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  patch({ zoomMode: "custom", zoomScale: Math.max(0.25, value.zoomScale / 1.25) });
                }}
              >
                Zoom Out
                <DropdownMenuShortcut>-</DropdownMenuShortcut>
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => patch({ zoomMode: "custom", zoomScale: 1 })}>
                Zoom to 100%
                <DropdownMenuShortcut>Ctrl+0</DropdownMenuShortcut>
              </DropdownMenuItem>
            </DropdownMenuSubContent>
          </DropdownMenuPortal>
        </DropdownMenuSub>

        {/* ── Guides ──────────────────────────────────────────────────────── */}
        <DropdownMenuSub>
          <DropdownMenuSubTrigger className="justify-between">
            <span>Guides</span>
            <span className="text-xs text-muted-foreground">
              {value.guidesRatio == null
                ? "Off"
                : GUIDE_RATIOS.find((r) => r.value === value.guidesRatio)?.label || "Custom"}
            </span>
          </DropdownMenuSubTrigger>
          <DropdownMenuPortal>
            <DropdownMenuSubContent className="w-40">
              <DropdownMenuLabel>Guides</DropdownMenuLabel>
              <DropdownMenuRadioGroup
                value={value.guidesRatio == null ? "off" : String(value.guidesRatio)}
                onValueChange={(v) => patch({ guidesRatio: v === "off" ? null : Number(v) })}
              >
                {GUIDE_RATIOS.map((r) => (
                  <DropdownMenuRadioItem key={r.label} value={String(r.value)}>
                    {r.label}
                  </DropdownMenuRadioItem>
                ))}
                <DropdownMenuRadioItem value="off">Off</DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
              <DropdownMenuSeparator />
              <DropdownMenuCheckboxItem
                checked={value.showMask}
                onCheckedChange={(v) => patch({ showMask: !!v })}
                disabled={value.guidesRatio == null}
              >
                Show Mask
              </DropdownMenuCheckboxItem>
            </DropdownMenuSubContent>
          </DropdownMenuPortal>
        </DropdownMenuSub>

        {/* ── View on Asset ───────────────────────────────────────────────── */}
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <span>View on Asset</span>
          </DropdownMenuSubTrigger>
          <DropdownMenuPortal>
            <DropdownMenuSubContent className="w-52">
              <DropdownMenuCheckboxItem
                checked={value.viewAnchoredComments}
                onCheckedChange={(v) => patch({ viewAnchoredComments: !!v })}
              >
                Anchored Comments
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={value.viewAnnotations}
                onCheckedChange={(v) => patch({ viewAnnotations: !!v })}
              >
                Annotations
              </DropdownMenuCheckboxItem>
            </DropdownMenuSubContent>
          </DropdownMenuPortal>
        </DropdownMenuSub>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Overlay for aspect-ratio guides. Uses a single SVG whose viewBox matches the
 * target aspect and preserveAspectRatio="xMidYMid meet" so the SVG contain-fits
 * inside the video container. The white rect drawn at viewBox bounds visually
 * outlines the fitted guide area with no manual measurement needed.
 *
 * showMask: dims OUTSIDE the guide box using 4 huge sibling rects placed just
 * outside the viewBox on each side. SVG elements render into their layout box
 * with overflow=visible by default, so the outer rects extend into the visible
 * area beyond the guide box, producing the letterbox mask effect. Pure CSS
 * would have needed container-aspect measurement — SVG dodges that.
 *
 * pointer-events-none so it never blocks video click-to-play.
 */
export function GuidesOverlay({
  ratio,
  showMask,
}: {
  ratio: number | null;
  showMask: boolean;
}) {
  if (ratio == null) return null;
  return (
    <svg
      viewBox={`0 0 ${ratio} 1`}
      preserveAspectRatio="xMidYMid meet"
      className="pointer-events-none absolute inset-0 h-full w-full"
      style={{ overflow: "visible" }}
    >
      {showMask && (
        <>
          {/* 4 dim rectangles that sit just outside the guide box on each edge.
              Their large extent past the viewBox pushes them past the fitted
              guide area into whatever container space is around it. */}
          <rect x={-ratio * 10} y={-10} width={ratio * 20 + ratio} height={10} fill="rgba(0,0,0,0.6)" />
          <rect x={-ratio * 10} y={1} width={ratio * 20 + ratio} height={10} fill="rgba(0,0,0,0.6)" />
          <rect x={-ratio * 10} y={0} width={ratio * 10} height={1} fill="rgba(0,0,0,0.6)" />
          <rect x={ratio} y={0} width={ratio * 10} height={1} fill="rgba(0,0,0,0.6)" />
        </>
      )}
      <rect
        x={0}
        y={0}
        width={ratio}
        height={1}
        fill="none"
        stroke="white"
        strokeOpacity={0.85}
        strokeWidth={2}
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

function zoomLabel(s: SettingsState) {
  if (s.zoomMode === "fit") return "Fit";
  if (s.zoomMode === "fill") return "Fill";
  return `${Math.round(s.zoomScale * 100)}%`;
}
